"use client";
import { OrderType, ServiceType } from "@/constants/enum";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import {
  ScheduleDateType,
  ScheduledTime,
  ScheduleTimeType,
} from "@/dto/scheduledTime";
import { Service } from "@/dto/service";
import pom from "@/utils/pom";
import {
  isWithinBusinessHours,
  getSchedulableDateOptions,
} from "@/utils/scheduleOrderUtils";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { DateTime } from "luxon";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useId, useState } from "react";
import { ConfirmButton } from "../button/confirmButton";
import { RegularModal } from "../modals/modals";
import { ScheduledDateDropdown } from "../scheduledDateDropdown/scheduledDateDropdown";
import { ScheduledTimeDropdown } from "../scheduledTimeDropdown/scheduledTimeDropdown";

interface OnLoadScheduleOrderModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  merchant: Merchant;
  selectedDayPart?: DayPart | null;
}

export const OnloadScheduledOrderModal = ({
  isOpen,
  onRequestClose,
  merchant,
  selectedDayPart,
}: OnLoadScheduleOrderModalProps) => {
  const t = useTranslations();
  const tPickup = useTranslations("CalculatePickTime");
  const searchParams = useSearchParams();
  const paramOrderType = searchParams?.get("order_type");
  const titleId = useId();
  const subtitleId = useId();
  const isDineIn =
    paramOrderType?.toLowerCase() === OrderType.DINE_IN.toLowerCase();
  const merchantService = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const curbsideService = merchant.services.find(
    (s: Service) => s.type === ServiceType.CURBSIDE
  );
  const deliveryService = merchant?.services.find(
    (s: Service) => s.type === ServiceType.DELIVERY
  );
  const now = DateTime.now().setZone(merchant?.timezone);
  const orderType = (() => {
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );
    const defaultOrderType = sessionTime?.orderType || OrderType.PICKUP;
    const restaurantServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.CURBSIDE, service: curbsideService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];
    const enabledServices = restaurantServices.filter(
      ({ service }) => service?.enabled
    );

    if (enabledServices.length >= 1) {
      return enabledServices[0].type;
    }
    return defaultOrderType;
  })();
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: t("CalculatePickTime.today"),
    dateObject: now.toJSDate(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now.toJSDate()),
  });

  const nowInMerchantZone = now;
  const businessHours = merchantService?.hours;

  const withinBusinessHours = isWithinBusinessHours(
    nowInMerchantZone,
    businessHours,
    merchant?.timezone
  );

  const schedulableDateOptions = getSchedulableDateOptions(
    merchant?.scheduledOrdering.maxDays - 1,
    businessHours,
    merchant.timezone,
    tPickup,
    merchant.leadTime,
    selectedDayPart
  );

  if (withinBusinessHours || isDineIn) {
    return null;
  }

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="focus:outline-none"
      overlayClassName="bg-opacity-75"
      data-testid={applyDataTestId(pom.onloadScheduledOrderModal.modal)}
      contentLabel={t("CalculatePickTime.dialogLabel")}
      focusElementId={titleId}
      contentClassName=" bg-white sm:rounded-lg shadow-lg w-full max-w-[600px] h-full sm:h-auto sm:w-[55%] mx-auto overflow-visible px-8 "
    >
      <h2
        id={titleId}
        tabIndex={-1}
        aria-describedby={subtitleId}
        className="text-#373737 font-semibold text-2xl pt-4 pr-4 focus:outline-none"
      >
        {t("homePage.noOnlineOrdersMsg", { merchantName: merchant.name })}
      </h2>
      {schedulableDateOptions.length > 0 ? (
        <>
          <p id={subtitleId} className="pt-4">
            {t("homePage.scheduleLaterMsg")}
          </p>
          <div className="py-12 flex flex-col sm:flex-row gap-4 w-full pb-6">
            <div className="w-full">
              <ScheduledDateDropdown
                scheduleDate={scheduleDate}
                setScheduleDate={setScheduleDate}
                scheduleDateRange={merchant?.scheduledOrdering?.maxDays - 1}
                setScheduleTime={setScheduleTime}
                businessHours={businessHours}
                timezone={merchant.timezone}
                leadTime={merchant.leadTime}
                selectedDayPart={selectedDayPart}
              />
            </div>
            <div className="w-full">
              <ScheduledTimeDropdown
                scheduleTime={scheduleTime}
                setScheduleTime={setScheduleTime}
                businessHours={businessHours}
                scheduleDate={scheduleDate}
                leadTime={merchant.leadTime}
                timezone={merchant.timezone}
                orderType={orderType}
                selectedDayPart={selectedDayPart}
              />
            </div>
          </div>
        </>
      ) : (
        <p id={subtitleId} className="pt-4 pb-4">
          {t("homePage.noScheduleMsg")}
        </p>
      )}
      {schedulableDateOptions.length > 0 && (
        <div>
          <ConfirmButton
            className="px-16 py-5 w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap rounded-lg mb-5"
            onClick={() => {
              saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
                orderType: orderType,
                date: scheduleDate,
                time: scheduleTime,
              });
              onRequestClose();
            }}
          >
            <p className="text-2xl font-medium">
              {t("homePage.scheduleAnOrder")}
            </p>
          </ConfirmButton>
        </div>
      )}
    </RegularModal>
  );
};
